import type { App, DefineComponent, Component, ComponentPublicInstance, Plugin } from 'vue';
import type { EConnectElementOptions } from './eConnectElementOptions';

import { defineCustomElement, defineSSRCustomElement } from 'vue';
import { addGlobalStylesToShadowRoot } from './globalStyles';
import { useLogger } from 'composables/useLogger';
import { globalPinia } from './globalPinia';
import { withFormSubmitter } from './withFormSubmitter';
import { SlotWhitespaceTrimmer } from './whitespaceTrimming';
import { camelToKebab } from './utils';
import { AttributeHandler } from './attributeHandler';
import { useStateStore } from 'composables/useStateStore';

type VueComponent =
  | DefineComponent<any, any, any, any, any, any, any, any, any, any>
  | Component<any, any, any>;

const { logger } = useLogger('defineEConnectElement', { level: 'debug' });

/**
 * Defines a custom element (web component) from a Vue component.
 *
 * This function wraps a Vue component with custom element lifecycle management,
 * providing enhanced features:
 * - Automatic prop parsing from `data-*` attributes with JSON support
 * - Two-way binding support with `v-model` normalization
 * - Form association capabilities for form controls
 * - Automatic cleanup and memory management
 * - Optional whitespace trimming for default slots
 * - Global stylesheet injection into shadow DOM
 * - Pinia store integration with automatic cleanup
 *
 * @param component - The Vue component to convert to a custom element
 * @param elementOptions - Configuration options for custom element behavior
 * @returns A CustomElementConstructor ready to be registered with `customElements.define()`
 *
 * @example
 * Basic usage:
 * ```typescript
 * import MyButton from './MyButton.vue';
 * import { defineEConnectElement } from '@/runtime/elements';
 *
 * const MyButtonElement = defineEConnectElement(MyButton);
 * customElements.define('my-button', MyButtonElement);
 * ```
 *
 * @example
 * With options:
 * ```typescript
 * const MyInputElement = defineEConnectElement(MyInput, {
 *   formAssociated: true,
 *   formSubmitter: true,
 *   trimDefaultSlotWhitespace: true,
 *   ssr: false
 * });
 * customElements.define('my-input', MyInputElement);
 * ```
 *
 * @example
 * Using data attributes for complex props:
 * ```html
 * <my-button
 *   data-config='{"theme": "primary", "size": "large"}'
 *   data-items='["one", "two", "three"]'
 * ></my-button>
 * ```
 */
export const defineEConnectElement = (
  component: VueComponent,
  elementOptions?: EConnectElementOptions
): CustomElementConstructor => {
  const pinia = globalPinia;

  const configApp = (app: App): void => {
    app.use(pinia as Plugin);
    app.config.errorHandler = (err: unknown, _: ComponentPublicInstance | null, info: string) => {
      logger.error(info, err);
    };
  };

  const customOptions = elementOptions?.customElementOptions ?? { shadowRoot: true };
  const element = elementOptions?.ssr
    ? defineSSRCustomElement(component, { configureApp: configApp, ...customOptions })
    : defineCustomElement(component, { configureApp: configApp, ...customOptions });

  if (elementOptions?.formAssociated) {
    (element as any).formAssociated = true;
  }

  const attributes = component?.props ? Object.keys(component.props) : [];
  const hasModelValueProp = !!component?.props && 'modelValue' in component.props;
  const dataAttrNames = attributes.map(attr => `data-${camelToKebab(attr)}`);
  const base = elementOptions?.formSubmitter ? withFormSubmitter(element) : element;
  const defaultTrim = elementOptions?.trimDefaultSlotWhitespace ?? true;

  /**
   * Enhanced custom element base class with EConnect-specific functionality.
   */
  class EConnectBase extends base {
    [key: string]: any;

    readonly #attributeHandler: AttributeHandler;
    #whitespaceTrimmer?: SlotWhitespaceTrimmer;
    #isDestroyed = false;
    #didParseDataset = false;
    #didInitTrim = false;

    constructor() {
      super();
      this.#attributeHandler = new AttributeHandler(this, attributes, hasModelValueProp);
    }

    /**
     * Defines which attributes the custom element should observe for changes.
     * Includes component props, data-* variants, and v-model attributes.
     */
    static get observedAttributes(): string[] {
      const extra = hasModelValueProp ? ['value', 'model-value'] : [];
      return Array.from(new Set([...attributes, ...extra, ...dataAttrNames]));
    }

    /**
     * Default setting for whitespace trimming in default slots.
     * Can be overridden per-instance via `data-trim-default-slot` attribute.
     */
    static readonly trimDefaultSlotWhitespace = defaultTrim;

    /**
     * Determines if default slot whitespace should be trimmed for this instance.
     * Checks instance-level `data-trim-default-slot` attribute first,
     * then falls back to the static class setting.
     */
    get #shouldTrimDefaultSlot(): boolean {
      // Prefer dataset (SonarQube), but fallback to getAttribute for Chrome timing issues
      const attr = this.dataset.trimDefaultSlot || this.getAttribute('data-trim-default-slot'); // NOSONAR
      if (attr != null) return attr !== 'false';
      const ctor = this.constructor as typeof EConnectBase;
      return ctor.trimDefaultSlotWhitespace;
    }

    /**
     * Called when the element is added to the DOM.
     * Handles attribute parsing, v-model normalization, whitespace trimming,
     * and global stylesheet injection.
     */
    connectedCallback(): void {
      this.#isDestroyed = false;

      if (!this.#didParseDataset) {
        this.#attributeHandler.parseDatasetAttributes();
        this.#didParseDataset = true;
      }

      if (hasModelValueProp) {
        this.#attributeHandler.normalizeModelValue();
      }

      super.connectedCallback();

      if (!this.#didInitTrim && this.#shouldTrimDefaultSlot && this.shadowRoot) {
        this.#whitespaceTrimmer = new SlotWhitespaceTrimmer(this, this.shadowRoot);

        if ('requestIdleCallback' in globalThis) {
          requestIdleCallback(() => this.#whitespaceTrimmer?.init(), { timeout: 100 });
        } else {
          queueMicrotask(() => this.#whitespaceTrimmer?.init());
        }
        this.#didInitTrim = true;
      }

      if (this.shadowRoot && !(this.shadowRoot as any).__styledOnce) {
        addGlobalStylesToShadowRoot(this.shadowRoot);
        (this.shadowRoot as any).__styledOnce = true;
      }
    }

    /**
     * Called when the element is removed from the DOM.
     * Performs cleanup of resources, listeners, and state.
     */
    disconnectedCallback(): void {
      if (this.#isDestroyed) return;
      this.#isDestroyed = true;
      this.#performTeardown();
      super.disconnectedCallback();
    }

    /**
     * Called when an observed attribute changes.
     * Delegates to specialized handlers for data-* and v-model attributes,
     * then forwards to Vue's custom element implementation.
     *
     * @param name - The attribute name that changed
     * @param oldValue - The previous attribute value
     * @param newValue - The new attribute value
     */
    attributeChangedCallback(name: string, oldValue?: string, newValue?: string): void {
      if (oldValue === newValue) return;

      this.#attributeHandler.handleDataAttributeChange(name, newValue);
      this.#attributeHandler.handleModelValueChange(name, newValue);

      const skipSuperForward = hasModelValueProp && name === 'value';
      if (!skipSuperForward) {
        this.#forwardToSuperAttributeChanged(name, oldValue, newValue);
      }
    }

    /**
     * Performs complete cleanup of all resources and state.
     * Called during disconnection to prevent memory leaks.
     */
    readonly #performTeardown = (): void => {
      this.#whitespaceTrimmer?.destroy();
      this.#cleanupComponentState();
    };

    /**
     * Removes component state from the shared Pinia store.
     * Uses the component's Vue instance UID for cleanup.
     */
    readonly #cleanupComponentState = (): void => {
      const uid = this._instance?.uid;
      if (!uid) return;

      try {
        const stateStore = useStateStore();
        stateStore.removeState(uid);
      } catch (err) {
        logger.error(`Failed to cleanup state for uid: ${uid}`, err);
      }
    };

    /**
     * Forwards attribute changes to Vue's custom element base class.
     * Safely handles cases where the base class doesn't implement
     * attributeChangedCallback.
     *
     * @param name - The attribute name that changed
     * @param oldValue - The previous attribute value
     * @param newValue - The new attribute value
     */
    readonly #forwardToSuperAttributeChanged = (
      name: string,
      oldValue?: string,
      newValue?: string
    ): void => {
      const superProto = Object.getPrototypeOf(EConnectBase.prototype) as {
        attributeChangedCallback?: (n: string, o?: string, v?: string) => void;
      };
      const superCb = superProto?.attributeChangedCallback;

      if (typeof superCb === 'function') {
        try {
          superCb.call(this, name, oldValue, newValue);
        } catch {
          /* Silently ignore errors from base class */
        }
      }
    };
  }

  return EConnectBase as unknown as CustomElementConstructor;
};
